import React, { useState } from 'react';
import useModalAccessibility from '../../hooks/useModalAccessibility';
import PasswordInput from '../PasswordInput';
import { formatPhone, normalizePhoneInput } from '../../utils/formatters';

const EditUserModal = ({ user, currentUser, onClose, onSave }) => {
  const modalRef = useModalAccessibility(onClose);
  const isEditingSelf = currentUser && user.id === currentUser.id;

  const [formData, setFormData] = useState({
    name: user.name,
    phone: user.phone ? formatPhone(user.phone) : '',
    role: user.role,
    status: user.status || 'active',
    password: '',
    tlc_license_number: user.tlc_license_number || '',
    years_experience: user.years_experience || '',
    fleet_name: user.fleet_name || '',
    medallion_numbers: user.medallion_numbers || '',
    garage_location: user.garage_location || '',
    bio: user.bio || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'phone' ? formatPhone(value) : value;
    setFormData(prev => ({ ...prev, [name]: next }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = { ...formData };
    if (!dataToSave.password || !dataToSave.password.trim()) {
      delete dataToSave.password;
    }
    // Normalize phone to digits if present; empty stays empty (admin can blank it out)
    if (dataToSave.phone && dataToSave.phone.trim()) {
      const phoneDigits = normalizePhoneInput(dataToSave.phone);
      if (phoneDigits.length !== 10) {
        alert('Phone number must be 10 digits');
        return;
      }
      dataToSave.phone = phoneDigits;
    }
    onSave(dataToSave);
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-user-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>
        <h2 id="edit-user-title">Edit User</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(212) 555-1234"
            />
          </div>

          <div className="form-group">
            <label>Role {isEditingSelf && <span className="field-note">(cannot change own role)</span>}</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={isEditingSelf}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-group">
            <label>Status {isEditingSelf && <span className="field-note">(cannot suspend own account)</span>}</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={isEditingSelf}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="form-group">
            <label>New Password <span className="field-note">(leave blank to keep current)</span></label>
            <PasswordInput
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password"
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          {formData.role !== 'admin' && (
            <>
              <div className="form-group">
                <label>TLC Licence No#</label>
                <input
                  type="text"
                  name="tlc_license_number"
                  value={formData.tlc_license_number}
                  onChange={(e) => { const val = e.target.value.replace(/\D/g, ''); handleChange({ target: { name: 'tlc_license_number', value: val } }); }}
                  placeholder="Numbers only"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <div className="form-group">
                <label>Years Experience</label>
                <input
                  type="number"
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Business Name</label>
                <input
                  type="text"
                  name="fleet_name"
                  value={formData.fleet_name}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Medallion Numbers</label>
                <input
                  type="text"
                  name="medallion_numbers"
                  value={formData.medallion_numbers}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Garage Location</label>
                <input
                  type="text"
                  name="garage_location"
                  value={formData.garage_location}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
